import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import type { Education as EducationType } from "common/types";

import { formatMonthAndYear, formatRange } from "../../utils";
import ExpandableText from "./ExpandableText";
import SectionContainer from "./SectionContainer";
import SectionItem from "./SectionItem";

import EducationFallbackImage from "assets/images/profile-placeholders/education.png";

export default function Education({
  education,
  isLoading,
}: {
  education: EducationType[];
  isLoading: boolean;
}) {
  return (
    <SectionContainer title="Education">
      {(isLoading ? new Array(3).fill({}) : education).map(
        (
          { start_date, end_date, school, degree, field_of_study, description },
          index,
        ) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: no unique key
          <SectionItem key={index}>
            <div className="flex flex-col">
              <div className="flex grow items-start gap-x-2">
                {isLoading ? (
                  <Skeleton className="size-10 rounded-2xl" />
                ) : (
                  <ProfileImage
                    src={school.logo_url}
                    className="rounded-2xl"
                    fallbackImage={EducationFallbackImage}
                  />
                )}
                <div className="flex flex-col gap-y-0.5">
                  {isLoading ? (
                    <Skeleton className="h-4 w-40" />
                  ) : (
                    <span className="break-all text-sm-bold">
                      {school.name}
                    </span>
                  )}
                  {isLoading ? (
                    <Skeleton className="h-3 w-60" />
                  ) : (
                    (degree || field_of_study) && (
                      <span className="break-all text-xs-medium text-black-500">
                        {[degree?.entity.name, field_of_study?.entity.name]
                          .filter((value) => !!value)
                          .join(", ")}
                      </span>
                    )
                  )}
                </div>
                {isLoading ? (
                  <Skeleton className="ml-auto h-3 w-20" />
                ) : (
                  (start_date || end_date) && (
                    <span className="ml-auto text-nowrap text-button-xxs text-black-500">
                      {formatRange(
                        start_date && formatMonthAndYear(start_date),
                        end_date ? formatMonthAndYear(end_date) : "Present",
                      )}
                    </span>
                  )
                )}
              </div>
              {description && (
                <ExpandableText className="ml-12">{description}</ExpandableText>
              )}
            </div>
          </SectionItem>
        ),
      )}
    </SectionContainer>
  );
}
